<template>
  <ion-page>
    <ion-header class="music-library-folder-header">
      <ion-toolbar>
        <ion-buttons slot="start">
          <ion-back-button default-href="/music-library" text="" />
        </ion-buttons>
        <ion-title>{{ folderTitle }}</ion-title>
      </ion-toolbar>
    </ion-header>
    <ion-content class="main-background-dark music-library-folder-content" :fullscreen="true">
      <div class="release-safe-area" />
      <div class="folder-meta-bar">
        <div class="duration-badge">
          <ion-icon :icon="timeOutline" />
          <span>{{ mediaUtils.formatTime(mediaUtils.getTotalPlaylistDuration(musicList)) }}</span>
        </div>
      </div>
      <div class="music-list-scroll">
        <ion-list class="bg-transparent music-library-track-list" lines="none">
          <ion-item
            v-for="(audioTrack, idx) in musicList"
            :key="audioTrack.id"
            class="music-item music-library-track-item"
            :class="{ playing: isTrackRowActive(idx) }"
            @click="selectTrack(idx)"
          >
            <div class="track-number">
              <span v-if="!isTrackRowActive(idx)">{{ idx + 1 }}</span>
              <ion-icon v-else :icon="play" class="playing-icon" />
            </div>
            <div class="track-info">
              <div class="track-name">{{ mediaUtils.getTrackName(audioTrack) }}</div>
              <div class="track-duration">{{ mediaUtils.formatTime(audioTrack.duration) }}</div>
            </div>
            <div class="track-actions">
              <div class="play-button-wrapper">
                <ion-icon :icon="isTrackRowActive(idx) ? pause : play" class="play-icon" />
              </div>
            </div>
          </ion-item>
        </ion-list>
        <div class="fake-footer" />
      </div>
    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import { PlayerBar } from '@/model/playerbar.model';
import { GoogleMedia } from '@/model/googlemedia.model';
import { GlobalContext } from '@/model/globalcontext.model';
import { PlayerModes } from '@/model/player.mode';
import { libraryMusicService } from '@/services/library-music.service';
import MediaUtils from '@/services/media.utils';
import FilesystemCustomUtils from '@/services/filesystem.custom.utils';
import {
  IonBackButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonList,
  IonPage,
  IonTitle,
  IonToolbar,
} from '@ionic/vue';
import { RmxAudioPlayer } from 'capacitor-plugin-playlist';
import { pause, play, timeOutline } from 'ionicons/icons';
import { defineComponent, inject } from 'vue';
// @ts-ignore
import { MittEmitter } from 'mitt-emitter';
import defaultCover from '@/theme/liga-logo-white.png';

export default defineComponent({
  name: 'MusicLibraryFolder',
  components: {
    IonPage,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonContent,
    IonList,
    IonItem,
    IonIcon,
    IonButtons,
    IonBackButton,
  },
  setup() {
    const playerBar = inject('playerBar') as PlayerBar;
    const audioPlayer = inject('audioPlayer') as RmxAudioPlayer;
    const emitter = inject('emitter') as MittEmitter;
    const globalContext = inject('globalContext') as GlobalContext;
    const mediaUtils = new MediaUtils();
    const filesystemCustomUtils = new FilesystemCustomUtils();
    return {
      playerBar,
      audioPlayer,
      emitter,
      globalContext,
      mediaUtils,
      filesystemCustomUtils,
      play,
      pause,
      timeOutline,
    };
  },
  data() {
    return {
      folderId: 0,
      folderTitle: '',
      musicList: [] as GoogleMedia[],
      isFirstSelect: true,
      coverSrc: defaultCover,
    };
  },
  ionViewWillEnter() {
    const id = Number(this.$route.params.folderId);
    this.folderId = id;
    this.playerBar.visible = this.playerBar.isReady && this.playerBar.musicList.length > 0;
    this.playerBar.currentMode = PlayerModes.EXTENDED;
  },
  ionViewDidEnter() {
    this.globalContext.isLoading = false;
  },
  mounted() {
    this.loadTracks();
    this.isFirstSelect = true;
    this.playerBar.visible = false;
    this.playerBar.isReady = false;
  },
  ionViewWillLeave() {
    this.playerBar.fixedAlbumArtUrl = '';
  },
  methods: {
    /** Пауза/подсветка строки только пока плеер на экране (или в микро-режиме), иначе после крестика остаётся play */
    isTrackRowActive(idx: number): boolean {
      return (
        !this.isFirstSelect &&
        this.playerBar.currentTrackIdx === idx &&
        !!(this.playerBar.visible || this.playerBar.microMode)
      );
    },
    async loadTracks() {
      const id = Number(this.$route.params.folderId);
      this.folderId = id;
      if (!id) return;
      try {
        const tracks = await libraryMusicService.listUserTracks(id);
        this.musicList = tracks;
        const folders = await libraryMusicService.listUserFolders();
        const f = folders.find((x) => x.id === id);
        this.folderTitle = f ? f.title : 'Музыка';
      } catch (e) {
        console.error(e);
        this.musicList = [];
      }
    },
    selectTrack(idx: number) {
      if (this.audioPlayer && this.audioPlayer.isPlaying) {
        this.audioPlayer.pause();
      }
      this.playerBar.visible = true;
      this.playerBar.isFM = false;
      this.playerBar.currentTrackIdx = idx;
      this.playerBar.playlistName = this.folderTitle;
      this.playerBar.programShortName = '';
      this.playerBar.playlistImage = '';
      this.playerBar.playlistImageDir = '';
      this.playerBar.fixedAlbumArtUrl = this.coverSrc;
      this.playerBar.offline = false;
      this.playerBar.offlineDirectory = `${this.filesystemCustomUtils.PLAYLISTS_DIRECTORY}/library_music_${this.folderId}`;

      if (this.isFirstSelect) {
        this.isFirstSelect = false;
        this.playerBar.musicList = this.musicList;
        this.emitter.emit('music-player-open-extended', true);
        this.emitter.emit('music-player-loaded-list', true);
      } else {
        this.emitter.emit('music-player-open-extended', true);
        this.emitter.emit('music-player-restart-and-play', true);
      }
    },
  },
});
</script>

<style scoped>
.music-library-folder-header ion-toolbar {
  --background: linear-gradient(135deg, #e85d04 0%, #dc2f02 100%);
  --border-width: 0;
  --color: #fff;
  --min-height: 56px;
}

.music-library-folder-header ion-title {
  color: #fff !important;
}

.music-library-folder-header ion-back-button {
  --color: #fff;
  --icon-margin-start: 4px;
}

.music-library-folder-content {
  --color: rgba(255, 255, 255, 0.92);
}

.release-safe-area {
  width: 100vw;
  height: var(--ion-safe-area-top);
}
.folder-meta-bar {
  display: flex;
  justify-content: center;
  padding: 10px 16px 6px;
  width: 100%;
  box-sizing: border-box;
}
.duration-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.1);
  padding: 0.4rem 1rem;
  border-radius: 20px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.85rem;
}
.music-library-track-list {
  padding: 4px 12px 8px;
  background: transparent !important;
  --background: transparent !important;
}

.music-list-scroll {
  max-height: calc(100vh - 180px);
  overflow-y: auto;
}
.fake-footer {
  height: 20vh;
}
.music-item {
  --background: rgba(255, 255, 255, 0.06);
  --background-hover: rgba(255, 255, 255, 0.1);
  --background-activated: rgba(255, 255, 255, 0.12);
  --padding-start: 16px;
  --padding-end: 16px;
  --min-height: 60px;
  --color: rgba(255, 255, 255, 0.95);
  margin-bottom: 10px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.music-item::part(native) {
  border-radius: 14px;
}

.track-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.playing-icon {
  color: var(--ion-color-orange) !important;
  font-size: 1.15rem;
}

.track-number {
  width: 28px;
  text-align: center;
  color: rgba(255, 255, 255, 0.72) !important;
}
.track-info {
  flex: 1;
  margin-left: 12px;
}
.track-name {
  color: #fff !important;
  font-weight: 500;
}
.track-duration {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.55) !important;
}
.play-icon {
  font-size: 1.4rem;
  color: var(--ion-color-orange) !important;
}
.play-button-wrapper {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(235, 89, 82, 0.18);
  border-radius: 50%;
}
.playing .play-button-wrapper {
  background: rgba(235, 89, 82, 0.28);
}
.playing .track-name {
  color: var(--ion-color-orange) !important;
}

@media (prefers-color-scheme: light) {
  .music-item {
    --background: rgba(255, 255, 255, 0.1);
    --background-hover: rgba(255, 255, 255, 0.14);
    --background-activated: rgba(255, 255, 255, 0.16);
    border-color: rgba(255, 255, 255, 0.14);
  }

  .duration-badge {
    background: rgba(255, 255, 255, 0.14);
  }
}
</style>
